import { GroupSecurity } from '@quromedical/fhir-common';
import type { GroupCreateRequest } from '@quromedical/models';
import { AddMemberCard, GroupFormCard, GroupMembers } from 'core/group';
import {
  Alert,
  Section,
  Skeleton,
  SkeletonProvider,
  Snackbar,
} from 'design-system';
import { useLocalSearchParams } from 'expo-router';
import { useUserSession } from 'hooks';
import { organizationFetcher } from 'integration';
import { GroupApi } from 'integration/aggregate';
import type { GroupStackScreenProps } from 'navigation';
import type React from 'react';
import { useCallback } from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import { NotFoundScreen } from 'screens/NotFoundScreen';
import { strings } from 'strings';
import { trpc } from 'trpc';

const api = new GroupApi();
const orgFetcher = organizationFetcher();

type RouteParams = {
  id: string;
};

export const GroupScreen: React.FC<GroupStackScreenProps<'View'>> = () => {
  const session = useUserSession();

  const canEdit = session.hasAny([GroupSecurity.GroupUpdate]);

  const { id } = useLocalSearchParams<RouteParams>();

  const {
    data = {},
    isFetching: isValidating,
    isError: error,
    refetch: revalidate,
  } = trpc.group.getById.useQuery({ id });

  const onSubmit = useCallback(
    async (formData: GroupCreateRequest) => {
      await api.updateGroup({ ...formData, id });
    },
    [id],
  );

  if (!id) {
    return <NotFoundScreen />;
  }

  return (
    <SkeletonProvider loading={isValidating}>
      <ScrollView>
        <Section unsetZIndex>
          <Skeleton>
            <GroupFormCard
              canEdit={canEdit}
              data={data}
              onSubmit={onSubmit}
              revalidate={revalidate}
              organizationFetcher={orgFetcher}
            />
          </Skeleton>
        </Section>

        <Section unsetZIndex>
          <Skeleton>
            <AddMemberCard id={id} />
          </Skeleton>
        </Section>
        <Section unsetZIndex>
          <Skeleton>
            <GroupMembers id={id} />
          </Skeleton>
        </Section>

        <Snackbar onClose={revalidate} isOpen={error}>
          <Alert
            backgroundColor="status-critical"
            textColor="white"
            onAction={revalidate}
            actionIcon="close"
            title={strings.ErrorCardTitle}
            body={strings.GenericErrorMessage}
          />
        </Snackbar>
      </ScrollView>
    </SkeletonProvider>
  );
};
